import { h } from '@/lib/dom';
import { data } from '@/lib/data';
import { currentSession } from '@/lib/session';
import { toast } from '@/components/Toast';
import { openModal } from '@/components/Modal';
import { ApiError } from '@/api/client';
import type { RouteContext } from '@/router';
import { navigate } from '@/router';
import { pageLayout } from './layout';
import type { LabSummary } from '@/api/types';
import { formatDate } from '@/lib/format';

interface UploadProgress {
  file: File;
  state: 'uploading' | 'parsing' | 'done' | 'error';
  progress: number;
  message?: string;
}

export async function renderLabUpload(_ctx: RouteContext): Promise<Node> {
  const user = currentSession();
  if (!user) {
    navigate('/login', { replace: true });
    return h('div');
  }
  if (user.role !== 'owner') {
    return pageLayout({
      user,
      active: 'labs',
      children: h('div', { class: 'empty-state' }, [
        h('p', null, 'Только владелец может загружать анализы.'),
        h('a', { href: '/labs', class: 'btn btn-secondary mt-2' }, '← К анализам'),
      ]),
    });
  }

  const dropzone = h('div', {
    class: 'dropzone',
    role: 'button',
    tabindex: '0',
    aria: { label: 'Перетащите PDF или нажмите для выбора файла' },
  }, [
    h('div', { class: 'dropzone-icon', 'aria-hidden': 'true' }, '⬆'),
    h('p', { style: 'font-size: 16px; margin-bottom: 8px;' }, 'Перетащите PDF сюда или нажмите, чтобы выбрать'),
    h('p', { class: 'text-tertiary', style: 'font-size: 13px;' }, 'PDF до 10 МБ. Можно несколько файлов за раз.'),
    h('button', { class: 'btn btn-primary mt-4', type: 'button' }, 'Выбрать файл'),
  ]);

  const fileInput = h('input', {
    type: 'file',
    accept: 'application/pdf',
    multiple: true,
    style: 'display: none;',
    aria: { hidden: 'true' },
  }) as HTMLInputElement;

  const uploadsEl = h('div', { id: 'uploads-list', class: 'mt-4' });
  const recentEl = h('div', { id: 'recent-uploads', class: 'mt-6' });

  const root = pageLayout({
    user,
    active: 'labs',
    children: [
      h('div', { class: 'breadcrumb' }, [
        h('a', { href: '/labs' }, '← Назад к анализам'),
      ]),
      h('div', { class: 'page-header' }, [
        h('h1', { class: 'page-title' }, 'Загрузить анализ'),
      ]),
      dropzone,
      fileInput,
      uploadsEl,
      h('h2', { class: 'mt-6 mb-2', style: 'font-size: 16px;' }, 'Последние загрузки'),
      recentEl,
    ],
  });

  dropzone.addEventListener('click', () => fileInput.click());
  dropzone.addEventListener('keydown', (ev: KeyboardEvent) => {
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      fileInput.click();
    }
  });
  dropzone.addEventListener('dragover', (ev) => {
    ev.preventDefault();
    dropzone.classList.add('dragover');
  });
  dropzone.addEventListener('dragleave', () => dropzone.classList.remove('dragover'));
  dropzone.addEventListener('drop', (ev) => {
    ev.preventDefault();
    dropzone.classList.remove('dragover');
    const files = ev.dataTransfer?.files;
    if (files?.length) handleFiles(Array.from(files));
  });
  fileInput.addEventListener('change', () => {
    if (fileInput.files?.length) handleFiles(Array.from(fileInput.files));
    fileInput.value = '';
  });

  function handleFiles(files: File[]): void {
    for (const f of files) {
      if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
        toast(`Можно загружать только PDF. Файл "${f.name}" пропущен.`, { type: 'error' });
        continue;
      }
      if (f.size > 10 * 1024 * 1024) {
        toast(`Файл "${f.name}" больше 10 МБ.`, { type: 'error' });
        continue;
      }
      void uploadOne(f);
    }
  }

  async function uploadOne(file: File): Promise<void> {
    const progress: UploadProgress = { file, state: 'uploading', progress: 0 };
    const row = renderUploadRow(progress);
    uploadsEl.appendChild(row);
    // simulate progress for UX while awaiting fetch
    let simulatedPct = 0;
    const timer = window.setInterval(() => {
      if (progress.state === 'uploading' && simulatedPct < 90) {
        simulatedPct += 10;
        progress.progress = simulatedPct;
        row.replaceWith(renderUploadRow(progress));
      }
    }, 200);
    try {
      const draft = await data.uploadLab(file);
      window.clearInterval(timer);
      progress.state = 'parsing';
      progress.progress = 100;
      const parsingRow = renderUploadRow(progress);
      row.replaceWith(parsingRow);
      await new Promise((res) => setTimeout(res, 400));
      progress.state = 'done';
      toast('Файл распознан. Проверьте значения.', { type: 'success' });
      navigate(`/labs/upload/${draft.lab_id}`);
    } catch (err) {
      window.clearInterval(timer);
      progress.state = 'error';
      progress.message = err instanceof Error ? err.message : 'Ошибка загрузки';
      row.replaceWith(renderUploadRow(progress));
      toast(progress.message, { type: 'error' });
    }
  }

  let labs: LabSummary[] = [];

  void loadRecent();
  async function loadRecent(): Promise<void> {
    try {
      labs = await data.listLabs();
      renderRecent();
      document.dispatchEvent(new Event('route:settled'));
    } catch {
      recentEl.innerHTML = '';
      recentEl.appendChild(h('p', { class: 'text-tertiary' }, 'Не удалось загрузить список.'));
    }
  }

  function renderRecent(): void {
    recentEl.innerHTML = '';
    if (!labs.length) {
      recentEl.appendChild(h('p', { class: 'text-tertiary' },
        'Загружать можно PDF от любой лаборатории. Система поддерживает Invitro, Гемотест, СИТИЛАБ, KDL, Хеликс и других — маркёры распознаёт AI.'));
    } else {
      recentEl.appendChild(recentList(labs, openDeleteModal));
    }
  }

  function openDeleteModal(lab: LabSummary): void {
    openModal({
      title: 'Удалить анализ?',
      size: 'sm',
      content: (close) => h('div', null, [
        h('p', null, buildDeleteMessage(lab)),
        h('div', { class: 'modal-footer modal-footer-inline' }, [
          h('button', {
            class: 'btn btn-secondary',
            type: 'button',
            on: { click: () => close() },
          }, 'Отмена'),
          h('button', {
            class: 'btn btn-danger',
            type: 'button',
            on: {
              click: async () => {
                close();
                // Откат возвращает одну строку на её место, а не снимок всего
                // массива: модалка закрывается до await, поэтому второе удаление
                // можно подтвердить, пока первый DELETE в полёте. Снимок
                // воскресил бы строку, которую второй запрос уже успешно снёс.
                // Место ищется заново — по соседу снизу, а не по сохранённому
                // индексу: за время запроса список мог сдвинуться.
                const index = labs.findIndex((l) => l.id === lab.id);
                if (index === -1) return;
                const removed = labs[index]!;
                const nextId = labs[index + 1]?.id ?? null;
                labs = labs.filter((l) => l.id !== lab.id);
                renderRecent();
                try {
                  await data.deleteLab(lab.id);
                  toast('Анализ удалён', { type: 'success' });
                } catch (err) {
                  if (err instanceof ApiError && err.status === 404) {
                    toast('Анализ уже был удалён', { type: 'info' });
                    return;
                  }
                  const at = nextId == null ? -1 : labs.findIndex((l) => l.id === nextId);
                  labs = at === -1
                    ? [...labs, removed]
                    : [...labs.slice(0, at), removed, ...labs.slice(at)];
                  renderRecent();
                  toast('Не удалось удалить анализ', { type: 'error' });
                }
              },
            },
          }, 'Удалить'),
        ]),
      ]),
    });
  }

  return root;
}

function renderUploadRow(p: UploadProgress): HTMLElement {
  const icon = p.state === 'done' ? '✓' : p.state === 'error' ? '⚠' : '⟳';
  return h('div', { class: 'upload-row' }, [
    h('span', { 'aria-hidden': 'true' }, icon + ' '),
    h('span', { style: 'flex: 0 0 auto; font-weight: 500;' }, p.file.name),
    h('span', { class: 'text-tertiary', style: 'font-size: 12px;' }, ` · ${(p.file.size / 1024 / 1024).toFixed(2)} МБ`),
    p.state === 'uploading' || p.state === 'parsing'
      ? h('div', { class: 'progress' }, h('div', {
          class: 'bar',
          style: `width: ${p.progress}%;`,
          'aria-label': p.state === 'uploading' ? 'Загрузка' : 'Распознавание',
        }))
      : null,
    p.state === 'parsing' ? h('span', { class: 'text-secondary' }, 'распознаём маркёры…') : null,
    p.state === 'error' ? h('span', { class: 'text-tertiary' }, p.message ?? '') : null,
  ]);
}

function recentList(labs: LabSummary[], onDelete: (lab: LabSummary) => void): HTMLElement {
  return h('ul', { class: 'recent-upload-list' },
    labs.slice(0, 10).map((lab) =>
      h('li', { class: 'recent-upload-row' }, [
        h('div', { class: 'flex items-center gap-3' }, [
          h('span', { class: 'text-tertiary' }, formatDate(lab.drawn_at)),
          h('span', null, lab.pdf_filename),
          h('span', { class: 'text-tertiary' }, ` · ${lab.total_results} маркёров`),
          lab.parse_status === 'confirmed'
            ? h('span', { style: 'color: var(--status-norm-fg);' }, '✓ подтверждено')
            : lab.parse_status === 'parsed'
            ? h('span', { style: 'color: var(--status-warn-fg);' }, '⚠ ожидает подтверждения')
            : h('span', { style: 'color: var(--text-tertiary);' }, lab.parse_status),
        ]),
        h('div', { class: 'recent-upload-actions' }, [
          lab.parse_status === 'parsed'
            ? h('a', {
                href: `/labs/upload/${lab.id}`,
                class: 'btn btn-ghost btn-sm',
              }, 'Открыть')
            : null,
          h('button', {
            class: 'btn btn-ghost btn-sm',
            type: 'button',
            aria: { label: `Удалить анализ «${lab.pdf_filename}»` },
            on: { click: () => onDelete(lab) },
          }, '🗑'),
        ]),
      ])));
}

function buildDeleteMessage(lab: LabSummary): string {
  const date = formatDate(lab.drawn_at, 'long');
  if (lab.total_results > 0) {
    const count = lab.total_results;
    const word = count % 10 === 1 && count % 100 !== 11 ? 'маркёром' : 'маркёрами';
    return `«${lab.pdf_filename}» (забор ${date}) будет удалён вместе с ${count} ${word} — они исчезнут из графиков и карточек. Это действие нельзя отменить.`;
  }
  return `«${lab.pdf_filename}» от ${date} будет удалён. Это действие нельзя отменить.`;
}
